import { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { fetchWithRefresh } from '../utils/auth';
import { extractSubdomain } from '../utils/auth';
import { useDispatch } from 'react-redux';
import { 
  setSelectedLanguages, 
  setAllLanguages, 
  setCurrentSchool, 
  setCurrentSubdomain, 
  setLanguage 
} from '../redux/slices/languageSlice';
import { updateBranding } from '../redux/slices/brandingSlice';
import { getLanguages, getSelectedLanguages, getPublicSelectedLanguages } from '../utils/api';
import languageService from '../utils/languageService';
import { useTranslation } from 'react-i18next';

// Define dynamic API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string means use relative URLs in production
  : 'http://localhost:5001';

// Define the production domain for subdomain detection
const PRODUCTION_DOMAIN = 'schoolmenus.xyz';

export const SchoolContext = createContext();

export const SchoolProvider = ({ children }) => {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubdomainRoute, setIsSubdomainRoute] = useState(false);
  const dispatch = useDispatch();
  const previousSubdomain = useRef(null);
  const { i18n } = useTranslation();
  
  // Helper function to update the user's current_school in localStorage
  const updateUserCurrentSchool = (schoolData, subdomain) => {
    try {
      // Get the current user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Only proceed if we have valid user data
      if (userData && Object.keys(userData).length > 0) {
        console.log('[SchoolContext] Updating user current_school in localStorage', { 
          oldSchool: userData.current_school?.id,
          newSchool: schoolData?.id,
          subdomain
        });
        
        // Update the current_school to match the subdomain's school
        if (schoolData) {
          userData.current_school = {
            id: schoolData.id,
            name: schoolData.name,
            subdomain: subdomain
          };
          console.log(`[SchoolContext] Setting user's current_school to: ${schoolData.id} (${subdomain})`);
        } else {
          // Store previous school before clearing it
          if (userData.current_school) {
            userData.previous_school = { ...userData.current_school };
            // Clear the current_school
            userData.current_school = null;
            console.log('[SchoolContext] Cleared user current_school for non-subdomain route');
          }
        }
        
        // Save the updated user data back to localStorage
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      console.error('[SchoolContext] Error updating user current_school:', err);
    }
  };
  
  // Function to detect and load school based on current location
  const detectSchool = async () => {
    try {
      // Get current pathname and use our utility to extract subdomain
      const pathname = window.location.pathname;
      const subdomain = extractSubdomain();
      
      // Check if this is a subdomain switching operation (not leaving a subdomain)
      const isSubdomainSwitching = sessionStorage.getItem('isSubdomainSwitching') === 'true';
      
      // If this is a subdomain switch, clear the flag now that we've detected it
      if (isSubdomainSwitching) {
        console.log('[SchoolContext] Detected subdomain switching operation - not treating as leaving subdomain');
        sessionStorage.removeItem('isSubdomainSwitching');
      }
      
      // Check if we're leaving a subdomain (only if not a subdomain switch)
      const leavingSubdomain = !isSubdomainSwitching && 
                               previousSubdomain.current && 
                               (!subdomain || previousSubdomain.current !== subdomain);
      
      // If subdomain hasn't changed, don't reload
      if (previousSubdomain.current === subdomain && !loading) {
        return;
      }
      
      console.log('[SchoolContext] Detecting school, current subdomain:', subdomain, 
                  'previous:', previousSubdomain.current,
                  'leaving subdomain:', leavingSubdomain,
                  'subdomain switching:', isSubdomainSwitching);
      
      // If we're leaving a subdomain, restore user's preferred language
      if (leavingSubdomain) {
        console.log('[SchoolContext] Leaving subdomain, restoring preferred language');
        
        // First try to reset directly to the preferred language (faster)
        const wasReset = await languageService.resetToPreferredLanguage(dispatch);
        
        if (!wasReset) {
          // Fall back to the full restoration process
          await languageService.restorePreferredLanguage(dispatch);
        }
        
        // Force clear any temporary language just to be certain
        languageService.clearTemporaryLanguage();
      }
      
      previousSubdomain.current = subdomain;
      
      // Root path check - forcibly set to non-subdomain route when on root path
      if (pathname === '/' || pathname === '') {
        console.log('[SchoolContext] Root path detected, forcing non-subdomain route');
        setIsSubdomainRoute(false);
        setSchool(null);
        
        // When on main domain, load all languages
        try {
          const allLanguagesData = await getLanguages();
          if (allLanguagesData) {
            dispatch(setAllLanguages(allLanguagesData));
            dispatch(setSelectedLanguages(allLanguagesData));
          }
        } catch (err) {
          console.error('[SchoolContext] Error fetching languages for root path:', err);
        }
        
        dispatch(setCurrentSchool(null));
        dispatch(setCurrentSubdomain(null));
        
        // Update user's current_school to null for non-subdomain route
        updateUserCurrentSchool(null, null);
        
        // Restore user's preferred language when on main domain
        const wasReset = await languageService.resetToPreferredLanguage(dispatch);
        
        if (!wasReset) {
          await languageService.restorePreferredLanguage(dispatch);
        }
        
        languageService.clearTemporaryLanguage();
        setLoading(false);
        return;
      }
      
      // Safety check to avoid 'undefined' as a literal subdomain
      if (subdomain === 'undefined') {
        console.warn('[SchoolContext] Detected "undefined" as subdomain, treating as no subdomain');
        setIsSubdomainRoute(false);
        setSchool(null);
        
        // When switching to no subdomain, fetch all languages
        try {
          const allLanguagesData = await getLanguages();
          if (allLanguagesData) {
            dispatch(setAllLanguages(allLanguagesData));
            dispatch(setSelectedLanguages(allLanguagesData));
          }
        } catch (err) {
          console.error('[SchoolContext] Error fetching languages for non-subdomain route:', err);
        }
        
        dispatch(setCurrentSchool(null));
        dispatch(setCurrentSubdomain(null));
        
        // Update user's current_school to null for non-subdomain route
        updateUserCurrentSchool(null, null);
        
        setLoading(false);
        return;
      }
      
      // Check if there's a saved school in sessionStorage
      const savedSchoolData = sessionStorage.getItem('currentSchool');
      
      // Determine if this is a subdomain route
      const isSubdomain = !!subdomain;
      setIsSubdomainRoute(isSubdomain);
      
      // Handle different scenarios for fetching school data
      if (subdomain) {
        // Case 1: Subdomain route - fetch school by subdomain
        console.log(`[SchoolContext] Loading school data for subdomain: ${subdomain}`);
        
        // Add timestamp for cache-busting
        const timestamp = Date.now();
        const schoolApiUrl = `${API_BASE_URL}/api/schools/${subdomain}?_t=${timestamp}`;
        console.log(`[SchoolContext] Fetching school data from: ${schoolApiUrl}`);
        
        let schoolData = null;
        let schoolFetchError = null;
        
        try {
          // First check if we have cached school data for this subdomain
          if (savedSchoolData) {
            try {
              const parsed = JSON.parse(savedSchoolData);
              if (parsed && parsed.subdomain === subdomain) {
                console.log('[SchoolContext] Using cached school data for quick load');
                setSchool(parsed);
                dispatch(setCurrentSchool(parsed.id));
                dispatch(setCurrentSubdomain(subdomain));
                schoolData = parsed; // Use cached data initially
              }
            } catch (e) {
              console.error('[SchoolContext] Error parsing saved school data:', e);
              // Continue with normal fetch
            }
          }
          
          const res = await fetch(schoolApiUrl, {
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            cache: 'no-store' // Force fetching a fresh response
          });
          
          if (!res.ok) {
            const errorText = await res.text();
            console.error(`[SchoolContext] School fetch failed: ${res.status}`, errorText);
            schoolFetchError = { status: res.status, text: errorText };
            
            // --- START: Check for recent previous subdomain --- 
            if (res.status === 404) {
              console.log(`[SchoolContext] School not found (404), checking if it's a recent previous subdomain: ${subdomain}`);
              try {
                const redirectCheckUrl = `${API_BASE_URL}/api/schools/find-current-subdomain/${subdomain}`;
                const redirectRes = await fetch(redirectCheckUrl);
                
                if (redirectRes.ok) {
                  const redirectData = await redirectRes.json();
                  if (redirectData.currentSubdomain) {
                    console.log(`[SchoolContext] Found current subdomain ${redirectData.currentSubdomain} for previous ${subdomain}. Redirecting...`);
                    // Redirect to the new subdomain path, preserving the rest of the URL
                    const newPath = `/${redirectData.currentSubdomain}${window.location.pathname.replace(/^\/[^/]+/, '')}${window.location.search}${window.location.hash}`;
                    console.log(`[SchoolContext] Redirecting to path: ${newPath}`);
                    window.location.href = newPath;
                    // Stop further processing as we are redirecting
                    return; 
                  }
                } else {
                  console.log(`[SchoolContext] No recent redirect found for ${subdomain} (Status: ${redirectRes.status})`);
                }
              } catch (redirectErr) {
                console.error('[SchoolContext] Error checking for redirect:', redirectErr);
              }
            }
            // --- END: Check for recent previous subdomain --- 
            
            // If using cached data, keep it despite the error, otherwise clear school
            if (!schoolData) {
              setSchool(null);
              setLoading(false);
            }
            // Don't return here if using cache, let the rest of the logic run with cached data
            if (!schoolData) return;
            
          } else {
            // Fetch was successful, update schoolData
            schoolData = await res.json();
            console.log('[SchoolContext] Received fresh school data for subdomain:', schoolData);
            // Cache the school data in sessionStorage
            sessionStorage.setItem('currentSchool', JSON.stringify(schoolData));
            // Set school data in state
            setSchool(schoolData);
          }
        } catch (err) {
          console.error('[SchoolContext] Error fetching school data:', err);
          schoolFetchError = { status: 500, text: err.message };
          // Clear school state on fetch error if not using cache
          if (!schoolData) {
             setSchool(null);
             setLoading(false);
             return;
          }
        }
        
        // --- This part now runs only if fetch was successful OR using cached data ---
        if (schoolData) {
           // Update Redux with current school context (even if using cache)
          dispatch(setCurrentSchool(schoolData.id));
          dispatch(setCurrentSubdomain(subdomain));
          
          // Update branding colors in Redux store
          if (schoolData.primary_bg_color || schoolData.primary_text_color || 
              schoolData.secondary_bg_color || schoolData.secondary_text_color) {
            dispatch(updateBranding({
              primaryBgColor: schoolData.primary_bg_color || '#4A90E2',
              primaryTextColor: schoolData.primary_text_color || '#FFFFFF',
              secondaryBgColor: schoolData.secondary_bg_color || '#3498DB',
              secondaryTextColor: schoolData.secondary_text_color || '#FFFFFF'
            }));
            console.log('[SchoolContext] Updated branding colors in Redux store');
          }
          
          // Update user's current_school in localStorage
          updateUserCurrentSchool(schoolData, subdomain);
          
          // Determine if user is authenticated
          const isAuthenticated = localStorage.getItem('accessToken') !== null;
          
          // Fetch selected languages for this school
          try {
            const selectedLanguagesData = isAuthenticated 
              ? await getSelectedLanguages(schoolData.id)
              : await getPublicSelectedLanguages(schoolData.id);
              
            console.log('[SchoolContext] School settings:', {
              schoolId: schoolData.id,
              schoolName: schoolData.name,
              defaultLanguageId: schoolData.default_language_id,
              userIsAuthenticated: isAuthenticated,
              selectedLanguages: selectedLanguagesData?.map(l => `${l.code} (${l.id})`) || []
            });
              
            if (selectedLanguagesData) {
              dispatch(setSelectedLanguages(selectedLanguagesData));
              
              // Check if current language is supported by this school
              const userPreferredLanguage = languageService.getPreferredLanguage();
              const isUserLanguageSupported = selectedLanguagesData.some(
                lang => lang.code === userPreferredLanguage
              );
              
              console.log('[SchoolContext] Language support check:', {
                userPreferredLanguage,
                isUserLanguageSupported,
                currentLanguage: i18n.language,
                temporaryLanguage: languageService.getTemporaryLanguage()
              });

              // Find the default language for this school
              const defaultLanguage = selectedLanguagesData.find(
                lang => lang.id === schoolData.default_language_id
              );
              
              console.log('[SchoolContext] School language details:', {
                defaultLanguageId: schoolData.default_language_id,
                defaultLanguage: defaultLanguage ? `${defaultLanguage.code} (${defaultLanguage.name})` : 'Not found',
                allAvailableLanguages: selectedLanguagesData.map(l => `${l.code} (${l.name})`)
              });
              
              if (!isUserLanguageSupported) {
                console.log('[SchoolContext] User preferred language is NOT supported by this school');
                
                // Check if default language exists
                if (defaultLanguage) {
                  console.log(`[SchoolContext] Will use school's default language: ${defaultLanguage.code} (${defaultLanguage.name})`);
                } else {
                  console.log('[SchoolContext] School default language not found in selected languages');
                }
                
                // Try to get school settings
                try {
                  // First try to use the school's default language
                  if (defaultLanguage) {
                    console.log(`[SchoolContext] Temporarily switching to school's default language: ${defaultLanguage.code}`);
                    await languageService.temporarilySwitchLanguage(defaultLanguage.code, dispatch);
                  }
                  // Fallback to first available language if default not found
                  else if (selectedLanguagesData.length > 0) {
                    const firstLang = selectedLanguagesData[0];
                    console.log(`[SchoolContext] Default language not found. Temporarily switching to first available language: ${firstLang.code}`);
                    await languageService.temporarilySwitchLanguage(firstLang.code, dispatch);
                  }
                } catch (err) {
                  console.error('[SchoolContext] Error handling language switch:', err);
                }
              } else {
                console.log(`[SchoolContext] User preferred language (${userPreferredLanguage}) is supported by this school - keeping current language`);
              }
            }
          } catch (err) {
            console.error('[SchoolContext] Error fetching selected languages for school:', err);
            
            // Fallback to all languages if can't get selected ones
            const allLanguagesData = await getLanguages();
            if (allLanguagesData) {
              dispatch(setSelectedLanguages(allLanguagesData));
            }
          }
        }
        // --- End of block that runs on success or with cache ---
        
      } else {
        // Case 2: Main domain - no specific school needed
        console.log('[SchoolContext] On main domain, setting school to null');
        setSchool(null);
        
        // When on main domain, load all languages
        try {
          const allLanguagesData = await getLanguages();
          if (allLanguagesData) {
            dispatch(setAllLanguages(allLanguagesData));
            dispatch(setSelectedLanguages(allLanguagesData));
          }
        } catch (err) {
          console.error('[SchoolContext] Error fetching languages for main domain:', err);
        }
        
        dispatch(setCurrentSchool(null));
        dispatch(setCurrentSubdomain(null));
        
        // Update user's current_school to null for non-subdomain route
        updateUserCurrentSchool(null, null);
        
        // Restore user's preferred language when on main domain
        // First try the direct reset (faster)
        const wasReset = await languageService.resetToPreferredLanguage(dispatch);
        
        if (!wasReset) {
          // Fall back to full restoration
          await languageService.restorePreferredLanguage(dispatch);
        }
        
        // Always clear any temporary language when on main domain
        languageService.clearTemporaryLanguage();
      }
    } catch (err) {
      console.error('[SchoolContext] Failed to detect school:', err);
      setSchool(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial detection
  useEffect(() => {
    console.log('[SchoolContext] Running initial school detection');
    detectSchool();
  }, [dispatch]); 
  
  // Listen for location changes
  useEffect(() => {
    console.log('[SchoolContext] Setting up location change listeners');
    
    // Function to handle path changes
    const handleLocationChange = () => {
      console.log('[SchoolContext] Location changed, re-detecting school');
      detectSchool();
    };

    // Create a MutationObserver to watch for URL changes
    const observer = new MutationObserver(() => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPathRef.current) {
        console.log(`[SchoolContext] Path changed from ${lastPathRef.current} to ${currentPath}`);
        lastPathRef.current = currentPath;
        handleLocationChange();
      }
    });

    // Set initial path
    const lastPathRef = { current: window.location.pathname };
    console.log('[SchoolContext] Initial path:', lastPathRef.current);

    // Start observing the document body for changes
    observer.observe(document.body, { childList: true, subtree: true });

    // Also listen for popstate events (back/forward button)
    window.addEventListener('popstate', handleLocationChange);

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);
  
  // Update branding whenever school changes
  useEffect(() => {
    if (school) {
      dispatch(updateBranding({
        primaryBgColor: school.primary_bg_color || '#4A90E2',
        primaryTextColor: school.primary_text_color || '#FFFFFF',
        secondaryBgColor: school.secondary_bg_color || '#3498DB',
        secondaryTextColor: school.secondary_text_color || '#FFFFFF'
      }));
      console.log('[SchoolContext] Updated branding colors from school change');
    }
  }, [school, dispatch]);
  
  // Function to refresh the current school data
  const refreshCurrentSchool = useCallback(async () => {
    try {
      if (!school || !school.id) {
        console.log('[SchoolContext] No current school to refresh');
        return;
      }
      
      console.log('[SchoolContext] Refreshing current school data');
      const subdomain = school.subdomain || extractSubdomain();
      
      if (!subdomain) {
        console.warn('[SchoolContext] Cannot refresh school - no subdomain available');
        return;
      }
      
      // Add a timestamp parameter to prevent browser disk caching
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/schools/${subdomain}?_t=${timestamp}`;
      console.log(`[SchoolContext] Fetching fresh school data from: ${url}`);
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store' // Force fetching a fresh response
      });
      
      if (!res.ok) {
        console.error(`[SchoolContext] Failed to refresh school data: ${res.status} ${res.statusText}`);
        return;
      }
      
      const freshSchoolData = await res.json();
      console.log('[SchoolContext] Received fresh school data:', freshSchoolData);
      
      // Update the state with fresh data
      setSchool(freshSchoolData);
      
      // Update sessionStorage cache
      sessionStorage.setItem('currentSchool', JSON.stringify(freshSchoolData));
      console.log('[SchoolContext] Updated sessionStorage with fresh school data');
      
      // Update Redux with current school context
      dispatch(setCurrentSchool(freshSchoolData.id));
      dispatch(setCurrentSubdomain(subdomain));
      
      // Update branding colors in Redux store
      if (freshSchoolData.primary_bg_color || freshSchoolData.primary_text_color || 
          freshSchoolData.secondary_bg_color || freshSchoolData.secondary_text_color) {
        dispatch(updateBranding({
          primaryBgColor: freshSchoolData.primary_bg_color || '#4A90E2',
          primaryTextColor: freshSchoolData.primary_text_color || '#FFFFFF',
          secondaryBgColor: freshSchoolData.secondary_bg_color || '#3498DB',
          secondaryTextColor: freshSchoolData.secondary_text_color || '#FFFFFF'
        }));
        console.log('[SchoolContext] Updated branding colors in Redux store after refresh');
      }
      
      console.log('[SchoolContext] School data refreshed successfully');
      return freshSchoolData;
    } catch (error) {
      console.error('[SchoolContext] Error refreshing school data:', error);
    }
  }, [school, dispatch]);
  
  return (
    <SchoolContext.Provider value={{ 
      school, 
      loading, 
      isSubdomainRoute,
      detectSchool,
      refreshCurrentSchool
    }}>
      {children}
    </SchoolContext.Provider>
  );
};

export const useSchool = () => useContext(SchoolContext); 